import {
  groupProductLinesByCategory,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'

export type WasteStockSource = 'main' | 'return' | 'damaged'

export type WasteCatalogStockItem = {
  productName: string
  source: WasteStockSource
  categoryName?: string
  categorySortOrder?: number
}

export type AggregatedProductOption = {
  productName: string
  source: WasteStockSource
  available: number
}

export type WasteDisplayGroup = {
  key: string
  productName: string
  stockSource: WasteStockSource
  reason?: string
  totalQty: number
  lots: { productionDate: string; qty: number }[]
  showLotBreakdown: boolean
}

export type ProposalChecklist = {
  hasItems: boolean
  reasonsOk: boolean
  stockOk: boolean
  ready: boolean
}

export function buildCatalogFromStockItems(items: WasteCatalogStockItem[]): PlanCatalogItem[] {
  const map = new Map<string, PlanCatalogItem>()
  for (const item of items) {
    const key = item.productName.trim().toLowerCase()
    const categoryName = item.categoryName?.trim()
    if (!categoryName || map.has(key)) continue
    map.set(key, {
      sku: item.productName,
      plan: 0,
      categoryName,
      categorySortOrder: item.categorySortOrder ?? 9999,
    })
  }
  return [...map.values()]
}

export function mergeProductCatalogs(...catalogs: PlanCatalogItem[][]): PlanCatalogItem[] {
  const map = new Map<string, PlanCatalogItem>()
  for (const catalog of catalogs) {
    for (const item of catalog) {
      const key = item.sku.trim().toLowerCase()
      if (!map.has(key)) map.set(key, item)
    }
  }
  return [...map.values()]
}

export function sortWasteProductOptions(
  options: AggregatedProductOption[],
  source: WasteStockSource,
): AggregatedProductOption[] {
  const sorted = [...options]
  if (source === 'return') {
    sorted.sort(
      (a, b) =>
        b.available - a.available || a.productName.localeCompare(b.productName, 'id'),
    )
    return sorted
  }
  sorted.sort((a, b) => a.productName.localeCompare(b.productName, 'id'))
  return sorted
}

export function groupWasteItemsForDisplay(
  items: Array<{
    productName: string
    stockSource: WasteStockSource
    productionDate: string
    qty: number
    reason?: string
  }>,
  showLotBreakdown: boolean,
): WasteDisplayGroup[] {
  const map = new Map<string, WasteDisplayGroup>()
  for (const item of items) {
    if (item.qty <= 0 || !item.productName.trim()) continue
    const key = `${item.stockSource}|${item.productName}`
    let group = map.get(key)
    if (!group) {
      group = {
        key,
        productName: item.productName,
        stockSource: item.stockSource,
        reason: item.reason,
        totalQty: 0,
        lots: [],
        showLotBreakdown: false,
      }
      map.set(key, group)
    }
    group.totalQty += item.qty
    if (item.reason?.trim() && !group.reason?.trim()) {
      group.reason = item.reason.trim()
    }
    if (item.productionDate?.trim()) {
      group.lots.push({ productionDate: item.productionDate.trim(), qty: item.qty })
    }
  }

  return [...map.values()]
    .sort((a, b) => a.productName.localeCompare(b.productName, 'id'))
    .map((group) => ({
      ...group,
      showLotBreakdown: showLotBreakdown && group.lots.length > 1,
    }))
}

export function getProposalChecklist(
  items: Array<{
    productName: string
    stockSource: WasteStockSource
    qty: number
    reason?: string
  }>,
  isGoodStock: (source: WasteStockSource) => boolean,
  stockOk: boolean,
): ProposalChecklist {
  const filled = items.filter((item) => item.qty > 0 && item.productName.trim())
  const hasItems = filled.length > 0
  const reasonsOk = filled.every(
    (item) => !isGoodStock(item.stockSource) || Boolean(item.reason?.trim()),
  )
  return {
    hasItems,
    reasonsOk,
    stockOk,
    ready: hasItems && reasonsOk && stockOk,
  }
}

export function groupAddItemOptionsByCategory(
  options: AggregatedProductOption[],
  catalog: PlanCatalogItem[],
) {
  const lines = options.map((option) => ({ productName: option.productName }))
  return groupProductLinesByCategory(lines, catalog).map((group) => ({
    categoryName: group.categoryName,
    categorySortOrder: group.categorySortOrder,
    items: group.items
      .map((line) => options.find((option) => option.productName === line.productName))
      .filter((option): option is AggregatedProductOption => Boolean(option)),
  }))
}
